import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Search, Globe, Shield, Building2, Plus, Edit2, Trash2, X, ChevronLeft,
  ExternalLink, Cloud, CloudOff, Lock, Filter, LayoutGrid, Server, Network,
  ShieldCheck, Activity, ArrowUpRight, ChevronDown, FileText, Newspaper, Link2, Unlink, Download,
} from "lucide-react";
import useIsMobile from "../hooks/useIsMobile";
import {
  cdbMeta, cdbSearch, cdbGetClients, cdbGetClient,
  cdbCreateClient, cdbUpdateClient, cdbDeleteClient,
  cdbGetWebsites, cdbGetWebsite, cdbCreateWebsite, cdbUpdateWebsite, cdbDeleteWebsite,
  cdbGetRules, cdbCreateRule, cdbUpdateRule, cdbDeleteRule,
  cdbCloudwaysMeta, cdbGetCloudwaysApps, cdbUpdateCloudwaysApp, cdbExport,
} from "../api";

/* ============================================================
   Client & Website Registry — internal staff lookup.
   Design language: deep-navy glassmorphism + neon indigo/violet/cyan,
   consuming the platform --t-* theme tokens so it tracks dark/light.
   ============================================================ */

const NEON = { indigo: "#4f6fff", violet: "#7b61ff", cyan: "#00d4ff", green: "#34d399", amber: "#fbbf24", red: "#f87171" };

// ── shared style atoms ───────────────────────────────────────
const panel = {
  background: "var(--t-panel)",
  border: "1px solid var(--t-border-soft)",
  borderRadius: 16,
  boxShadow: "var(--t-shadow-panel)",
};
const glassPanel = {
  ...panel,
  borderColor: "rgba(79,111,255,0.28)",
  boxShadow: "var(--t-shadow-panel), 0 0 30px rgba(79,111,255,0.07)",
};
const inputStyle = {
  width: "100%", padding: "9px 12px", fontSize: 13,
  background: "var(--t-input)", color: "var(--t-text-primary)",
  border: "1px solid var(--t-border-soft)", borderRadius: 10, outline: "none",
};
const labelStyle = { fontSize: 10.5, fontWeight: 700, color: "var(--t-text-label)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5, display: "block" };
const mono = { fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace" };
const thStyle = { textAlign: "left", padding: "10px 13px", fontSize: 10.5, fontWeight: 700, color: "var(--t-text-label)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--t-border-soft)", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--t-panel)", zIndex: 1 };
const thSortable = { ...thStyle, cursor: "pointer", userSelect: "none" };
const tdStyle = { padding: "10px 13px", fontSize: 13, color: "var(--t-text-body)", borderBottom: "1px solid var(--t-border-softer)", verticalAlign: "middle" };

const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px",
  fontSize: 13, fontWeight: 600, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer",
  background: `linear-gradient(135deg, ${NEON.indigo}, ${NEON.violet})`,
  boxShadow: "0 4px 18px rgba(79,111,255,0.45), inset 0 1px 0 rgba(255,255,255,0.18)",
  whiteSpace: "nowrap",
};
const btnGhost = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 13px",
  fontSize: 13, fontWeight: 500, color: "var(--t-text-body)", cursor: "pointer",
  background: "rgba(79,111,255,0.08)", border: "1px solid rgba(79,111,255,0.25)", borderRadius: 10,
  whiteSpace: "nowrap",
};
const btnDanger = { ...btnGhost, color: "var(--badge-red)", borderColor: "rgba(248,113,113,0.32)", background: "rgba(248,113,113,0.07)" };
const iconBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, cursor: "pointer", border: "1px solid var(--t-border-soft)", background: "var(--t-input)", color: "var(--t-text-muted)" };

// Scoped CSS for animations + hover affordances the inline styles can't express.
function ScopedStyles() {
  return (
    <style>{`
      @keyframes cdb-grad { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
      @keyframes cdb-rise { from{opacity:0; transform:translateY(6px)} to{opacity:1; transform:none} }
      @keyframes cdb-pulse { 0%,100%{opacity:.55} 50%{opacity:1} }
      .cdb-rise { animation: cdb-rise .28s ease both; }
      .cdb-row { transition: background .12s; }
      .cdb-row:hover { background: rgba(79,111,255,0.06); }
      .cdb-card { transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease; }
      .cdb-card:hover { transform: translateY(-3px); box-shadow: var(--t-shadow-panel), 0 0 26px rgba(79,111,255,0.20); border-color: rgba(79,111,255,0.45) !important; }
      .cdb-kpi:hover { transform: translateY(-2px); }
      .cdb-tab { transition: color .15s, background .15s; }
      .cdb-input:focus { border-color: rgba(79,111,255,0.6) !important; box-shadow: 0 0 0 3px rgba(79,111,255,0.14); }
      .cdb-scroll { max-height: 64vh; overflow: auto; }
      .cdb-scroll::-webkit-scrollbar { width: 9px; height: 9px; }
      .cdb-scroll::-webkit-scrollbar-thumb { background: rgba(79,111,255,0.3); border-radius: 8px; }
    `}</style>
  );
}

// ── badges & pills ───────────────────────────────────────────
function CloudflareBadge({ value }) {
  const on = value && value !== "No";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
      color: on ? "#03212f" : "var(--t-text-muted)",
      background: on ? `linear-gradient(135deg, ${NEON.cyan}, #38bdf8)` : "var(--t-input)",
      border: on ? "none" : "1px solid var(--t-border-soft)",
      boxShadow: on ? "0 0 12px rgba(0,212,255,0.4)" : "none",
    }}>
      {on ? <Cloud size={12} /> : <CloudOff size={12} />}{value || "—"}
    </span>
  );
}
function Pill({ text, color = "var(--t-text-muted)", bg = "var(--t-input)", border }) {
  if (!text) return <span style={{ color: "var(--t-text-faint)" }}>—</span>;
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, color, background: bg, border: border || "none", whiteSpace: "nowrap" }}>{text}</span>;
}
const STATUS_COLORS = {
  Live: { c: NEON.green, b: "rgba(52,211,153,0.13)" },
  Staging: { c: NEON.amber, b: "rgba(251,191,36,0.13)" },
  Dev: { c: "#5b8cff", b: "rgba(91,140,255,0.13)" },
  Suspended: { c: NEON.red, b: "rgba(248,113,113,0.13)" },
};
const SSL_COLORS = {
  Active: { c: NEON.green, b: "rgba(52,211,153,0.12)" },
  Pending: { c: NEON.amber, b: "rgba(251,191,36,0.12)" },
  Expired: { c: NEON.red, b: "rgba(248,113,113,0.12)" },
  None: { c: "var(--t-text-muted)", b: "var(--t-input)" },
};
const StatusPill = ({ value }) => { const s = STATUS_COLORS[value]; return <Pill text={value} color={s?.c} bg={s?.b} />; };
const SslPill = ({ value }) => { const s = SSL_COLORS[value]; return <Pill text={value} color={s?.c} bg={s?.b} />; };
const RuleTypePill = ({ value }) => <Pill text={value} color={NEON.violet} bg="rgba(123,97,255,0.13)" border="1px solid rgba(123,97,255,0.22)" />;

// ── Cloudways-specific badges ────────────────────────────────
// "Live target" — where the site's traffic currently points during the
// WP Engine → Cloudways migration.
const LIVE_COLORS = {
  Cloudways:     { c: NEON.green, b: "rgba(52,211,153,0.14)" },
  WPE:           { c: NEON.amber, b: "rgba(251,191,36,0.14)" },
  "Staging/Dev": { c: "var(--t-text-muted)", b: "var(--t-input)" },
  Review:        { c: NEON.violet, b: "rgba(123,97,255,0.14)" },
};
const LiveTargetPill = ({ value }) => {
  const s = LIVE_COLORS[value];
  return <Pill text={value === "WPE" ? "WP Engine" : value} color={s?.c} bg={s?.b} />;
};
// GPS editorial-platform access marker. `on` truthy → neon chip; otherwise faint dash.
function GpsBadge({ on, compact }) {
  if (!on) return compact ? null : <span style={{ color: "var(--t-text-faint)" }}>—</span>;
  return (
    <span title="Uses the GPS editorial platform" style={{
      display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 20,
      fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap", color: "#03261a",
      background: `linear-gradient(135deg, ${NEON.green}, #10b981)`, boxShadow: "0 0 11px rgba(52,211,153,0.4)",
    }}><Newspaper size={11} /> GPS</span>
  );
}
// Small "manage on Cloudways" launcher.
function CloudwaysLink({ url, label = "Manage", small }) {
  if (!url) return <span style={{ color: "var(--t-text-faint)" }}>—</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
      title="Open this application in the Cloudways console"
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, padding: small ? "3px 8px" : "6px 12px",
        fontSize: small ? 11 : 12.5, fontWeight: 600, borderRadius: 9, textDecoration: "none",
        color: NEON.cyan, background: "rgba(0,212,255,0.10)", border: "1px solid rgba(0,212,255,0.28)", whiteSpace: "nowrap",
      }}>
      <Server size={small ? 11 : 13} /> {label} <ExternalLink size={small ? 10 : 11} />
    </a>
  );
}

// Detail block shown inside a website's expanded row / detail view when the
// site is linked to one or more Cloudways applications.
function CloudwaysAppBlock({ apps }) {
  return (
    <div>
      <div style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <Server size={12} /> Cloudways application{apps.length > 1 ? "s" : ""} ({apps.length})
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {apps.map((a) => (
          <div key={a.id} style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
            background: "var(--t-panel)", border: "1px solid var(--t-border-softer)", borderRadius: 10, padding: "11px 14px" }}>
            <div style={{ display: "flex", flexDirection: "column", minWidth: 130 }}>
              <span style={{ fontSize: 9.5, color: "var(--t-text-label)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Server</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--t-text-primary)" }}>{a.server_label || "—"}</span>
              <span style={{ ...mono, fontSize: 11, color: "var(--t-text-muted)" }}>{a.server_ip || ""}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: 9.5, color: "var(--t-text-label)", textTransform: "uppercase", letterSpacing: "0.05em" }}>App ID</span>
              <span style={{ ...mono, fontSize: 13, color: "var(--t-text-body)" }}>{a.app_id}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: 9.5, color: "var(--t-text-label)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Live target</span>
              <LiveTargetPill value={a.live_target} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <span style={{ fontSize: 9.5, color: "var(--t-text-label)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>GPS</span>
              {a.gps_access ? <GpsBadge on /> : <span style={{ fontSize: 12, color: "var(--t-text-faint)" }}>No</span>}
            </div>
            <div style={{ flex: 1 }} />
            <CloudwaysLink url={a.manage_url} label="Manage on Cloudways" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── selection + Excel export (shared across all tabs) ────────
const CB_STYLE = { width: 16, height: 16, cursor: "pointer", accentColor: NEON.indigo, verticalAlign: "middle" };
// Tracks a Set of selected row PKs; survives re-filtering.
function useRowSelection() {
  const [selected, setSelected] = useState(() => new Set());
  return {
    selected,
    has: (id) => selected.has(id),
    size: selected.size,
    ids: () => [...selected],
    toggleOne: (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }),
    setMany: (idList, on) => setSelected((s) => { const n = new Set(s); idList.forEach((id) => on ? n.add(id) : n.delete(id)); return n; }),
    clear: () => setSelected(new Set()),
  };
}
async function downloadCdbExport(type, ids) {
  const r = await cdbExport(type, ids);
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const a = document.createElement("a");
  a.href = url; a.download = `${type}-${ids.length}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
// Export button — disabled until at least one row is selected.
function ExportButton({ type, ids }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!ids.length) return;
    setBusy(true);
    try { await downloadCdbExport(type, ids); } catch (e) { alert(e?.response?.data?.error || "Export failed"); }
    finally { setBusy(false); }
  };
  const enabled = ids.length > 0 && !busy;
  return (
    <button onClick={run} title="Export selected rows to Excel"
      style={{ ...btnPrimary, opacity: enabled ? 1 : 0.5, pointerEvents: enabled ? "auto" : "none" }}>
      <Download size={15} /> {busy ? "Exporting…" : `Export${ids.length ? ` (${ids.length})` : ""}`}
    </button>
  );
}
// Header "select all visible" checkbox (indeterminate when partial).
function SelectAllBox({ ids, sel }) {
  const all = ids.length > 0 && ids.every((id) => sel.has(id));
  const some = ids.some((id) => sel.has(id)) && !all;
  return <input type="checkbox" style={CB_STYLE} checked={all}
    ref={(el) => { if (el) el.indeterminate = some; }}
    onChange={() => sel.setMany(ids, !all)} title="Select all (filtered)" />;
}
// Inline "N selected · Clear" strip for a table/grid header.
function SelectionInfo({ sel }) {
  if (!sel.size) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <span style={{ color: NEON.cyan, fontWeight: 600 }}>{sel.size} selected</span>
      <button style={{ ...btnGhost, padding: "4px 10px" }} onClick={sel.clear}>Clear selection</button>
    </span>
  );
}

// ── modal & form atoms ───────────────────────────────────────
function Modal({ title, subtitle, onClose, children, width = 580 }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(2,6,18,0.74)", backdropFilter: "blur(5px)", zIndex: 9999, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "6vh 16px", overflowY: "auto" }}>
      <div className="cdb-rise" style={{ ...glassPanel, width: "100%", maxWidth: width, boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 0 30px rgba(79,111,255,0.14)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--t-border-soft)" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--t-text-primary)" }}>{title}</h3>
            {subtitle && <div style={{ fontSize: 12, color: "var(--t-text-label)", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ ...iconBtn, border: "none", background: "transparent" }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}
const Field = ({ label, required, children, span }) => (
  <div style={{ marginBottom: 14, gridColumn: span ? `span ${span}` : undefined }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: NEON.violet }}> *</span>}</label>
    {children}
  </div>
);
const Input = (p) => <input {...p} className="cdb-input" style={{ ...inputStyle, ...(p.style || {}) }} />;
const Textarea = (p) => <textarea {...p} className="cdb-input" style={{ ...inputStyle, minHeight: 76, resize: "vertical", ...(p.style || {}) }} />;
function Select({ value, onChange, options, placeholder = "— none —" }) {
  return (
    <select value={value || ""} onChange={onChange} className="cdb-input" style={{ ...inputStyle, appearance: "auto" }}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
const FormError = ({ msg }) => msg ? (
  <div style={{ display: "flex", gap: 8, alignItems: "center", color: "var(--badge-red)", fontSize: 12.5, marginBottom: 14, padding: "9px 12px", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)", borderRadius: 9 }}>
    <Shield size={14} /> {msg}
  </div>
) : null;

/* ============================================================
   Main component
   ============================================================ */
export default function ClientDatabase() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { id: routeClientId } = useParams();

  const [enums, setEnums] = useState(null);
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("overview");
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState("");
  const [results, setResults] = useState({ sites: [], rules: [] });
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => { cdbMeta().then((r) => setEnums(r.data.enums)).catch(() => {}); }, []);
  const loadClients = useCallback(() => cdbGetClients().then((r) => setClients(r.data)).catch(() => {}), []);
  useEffect(() => { loadClients(); }, [loadClients]);

  const runSearch = useCallback(async (q, type) => {
    setSearching(true);
    try {
      const r = await cdbSearch({ q, type: type || undefined });
      setResults({ sites: r.data.sites || [], rules: r.data.rules || [] });
    } finally { setSearching(false); }
  }, []);
  useEffect(() => {
    if (tab !== "search") return;
    const t = setTimeout(() => runSearch(query, searchType), 200);
    return () => clearTimeout(t);
  }, [query, searchType, tab, runSearch]);

  // "/" focuses the global search from anywhere on the page.
  useEffect(() => {
    const h = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA" && document.activeElement?.tagName !== "SELECT") {
        e.preventDefault(); searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  if (routeClientId) {
    return <ClientDetail clientPk={routeClientId} enums={enums} clients={clients}
      onBack={() => navigate("/client-database")} reloadClients={loadClients} />;
  }

  const TABS = [
    { key: "overview", label: "Overview", icon: LayoutGrid },
    { key: "search", label: "Search", icon: Search },
    { key: "clients", label: "Clients", icon: Building2 },
    { key: "websites", label: "Websites", icon: Globe },
    { key: "cloudways", label: "Cloudways", icon: Server },
    { key: "rules", label: "Rules", icon: Shield },
  ];

  return (
    <div style={{ padding: isMobile ? 14 : 28, width: "100%", maxWidth: 1840, margin: "0 auto", boxSizing: "border-box" }}>
      <ScopedStyles />

      {/* ── Branded hero ── */}
      <div style={{
        ...glassPanel, padding: isMobile ? 18 : "22px 26px", marginBottom: 18, position: "relative", overflow: "hidden",
        background: "linear-gradient(120deg, var(--t-panel) 0%, var(--t-panel-hi) 100%)",
      }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.5, pointerEvents: "none",
          backgroundImage: "radial-gradient(circle at 88% -20%, rgba(0,212,255,0.16), transparent 42%), radial-gradient(circle at 8% 120%, rgba(123,97,255,0.16), transparent 45%)" }} />
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, flexShrink: 0, overflow: "hidden",
            background: `linear-gradient(135deg, ${NEON.indigo}, ${NEON.violet})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 26px rgba(79,111,255,0.55), inset 0 1px 0 rgba(255,255,255,0.28)",
          }}>
            <img src="/logo.jpg" alt="MIDTC" style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => { e.target.outerHTML = '<span style="color:#fff;font-weight:800;font-size:24px">M</span>'; }} />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h1 style={{
              margin: 0, fontSize: isMobile ? 21 : 25, fontWeight: 800, letterSpacing: -0.5,
              background: "linear-gradient(90deg, var(--t-text-primary) 30%, #6ea8ff 70%, #00d4ff 100%)",
              backgroundSize: "200% auto", animation: "cdb-grad 8s linear infinite",
              WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>Client &amp; Website Registry</h1>
            <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "var(--t-text-label)", display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: NEON.cyan }}><Lock size={12} /> MIDTC internal</span>
              <span style={{ opacity: 0.4 }}>•</span>
              <span>{clients.length} clients indexed</span>
            </p>
          </div>
        </div>

        {/* Global search — the headline feature */}
        <div style={{ position: "relative", marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
            <Search size={17} style={{ position: "absolute", left: 14, top: 13, color: NEON.cyan, pointerEvents: "none" }} />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); if (tab !== "search") setTab("search"); }}
              onFocus={() => { if (tab !== "search") setTab("search"); }}
              placeholder="Search clients, domains, DNS, hosting, status, notes, rules…"
              className="cdb-input"
              style={{ ...inputStyle, height: 46, paddingLeft: 42, paddingRight: 70, fontSize: 14.5, borderRadius: 12, background: "var(--t-input-hi, var(--t-input))" }}
            />
            {!query && <kbd style={{ position: "absolute", right: 14, top: 13, fontSize: 11, color: "var(--t-text-faint)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "2px 7px", ...mono }}>/</kbd>}
          </div>
          {tab === "search" && (
            <div style={{ display: "flex", gap: 6 }}>
              {[["", "All"], ["sites", "Sites"], ["rules", "Rules"]].map(([v, l]) => (
                <button key={v} onClick={() => setSearchType(v)} className="cdb-tab" style={{
                  ...btnGhost, padding: "9px 16px",
                  ...(searchType === v ? { background: `linear-gradient(135deg, ${NEON.indigo}, ${NEON.violet})`, color: "#fff", border: "none", boxShadow: "0 4px 14px rgba(79,111,255,0.4)" } : {}),
                }}>{l}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 18, overflowX: "auto", paddingBottom: 2 }}>
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} className="cdb-tab" style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 17px", fontSize: 13.5, fontWeight: active ? 700 : 500,
              cursor: "pointer", whiteSpace: "nowrap", borderRadius: 11,
              color: active ? "#fff" : "var(--t-text-muted)",
              background: active ? `linear-gradient(135deg, ${NEON.indigo}, ${NEON.violet})` : "var(--t-input)",
              border: active ? "none" : "1px solid var(--t-border-soft)",
              boxShadow: active ? "0 4px 16px rgba(79,111,255,0.4)" : "none",
            }}>
              <t.icon size={15} />{t.label}
            </button>
          );
        })}
      </div>

      <div className="cdb-rise" key={tab}>
        {tab === "overview" && <Overview clients={clients} navigate={navigate} setTab={setTab} />}
        {tab === "search" && <SearchResults results={results} searching={searching} query={query} navigate={navigate} />}
        {tab === "clients" && <ClientsTab clients={clients} reload={loadClients} navigate={navigate} />}
        {tab === "websites" && <WebsitesTab clients={clients} enums={enums} navigate={navigate} />}
        {tab === "cloudways" && <CloudwaysTab navigate={navigate} />}
        {tab === "rules" && <RulesTab clients={clients} enums={enums} />}
      </div>
    </div>
  );
}

/* ── Overview dashboard ───────────────────────────────────── */
function Overview({ clients, navigate, setTab }) {
  const [websites, setWebsites] = useState(null);
  const [cwApps, setCwApps] = useState(null);
  useEffect(() => { cdbGetWebsites({}).then((r) => setWebsites(r.data)).catch(() => setWebsites([])); }, []);
  useEffect(() => { cdbGetCloudwaysApps({}).then((r) => setCwApps(r.data)).catch(() => setCwApps([])); }, []);

  const totals = useMemo(() => {
    const sites = websites || [];
    const apps = cwApps || [];
    const cf = sites.filter((w) => w.cloudflare && w.cloudflare !== "No").length;
    const rules = clients.reduce((n, c) => n + Number(c.rules_count || 0), 0);
    const gps = apps.filter((a) => a.gps_access).length;
    return { clients: clients.length, sites: sites.length, cf, rules, cfPct: sites.length ? Math.round((cf / sites.length) * 100) : 0,
      cwApps: apps.length, gps };
  }, [websites, cwApps, clients]);

  const dist = useCallback((key) => {
    const sites = websites || [];
    const m = {};
    sites.forEach((w) => { const k = w[key] || "—"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [websites]);

  const cwDist = useCallback((key) => {
    const apps = cwApps || [];
    const m = {};
    apps.forEach((a) => { const k = a[key] || "—"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [cwApps]);

  const topClients = useMemo(() => [...clients].sort((a, b) => Number(b.websites_count) - Number(a.websites_count)).slice(0, 6), [clients]);

  const KPIS = [
    { label: "Clients", value: totals.clients, icon: Building2, color: NEON.indigo, onClick: () => setTab("clients") },
    { label: "Websites", value: totals.sites, icon: Globe, color: "#5b8cff", onClick: () => setTab("websites") },
    { label: "Cloudways Apps", value: totals.cwApps, icon: Server, color: NEON.cyan, onClick: () => setTab("cloudways") },
    { label: "GPS Sites", value: totals.gps, sub: "use GPS platform", icon: Newspaper, color: NEON.green, onClick: () => setTab("cloudways") },
    { label: "On Cloudflare", value: totals.cf, sub: `${totals.cfPct}% of sites`, icon: Cloud, color: "#38bdf8", onClick: () => setTab("websites") },
    { label: "Security Rules", value: totals.rules, icon: ShieldCheck, color: NEON.violet, onClick: () => setTab("rules") },
  ];

  return (
    <div>
      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 18 }}>
        {KPIS.map((k) => (
          <div key={k.label} onClick={k.onClick} className="cdb-card cdb-kpi" style={{
            ...panel, padding: 18, cursor: "pointer", position: "relative", overflow: "hidden",
            borderTop: `2px solid ${k.color}`,
          }}>
            <div style={{ position: "absolute", top: -20, right: -20, width: 90, height: 90, borderRadius: "50%", background: `radial-gradient(circle, ${k.color}22, transparent 70%)` }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: `${k.color}1c`, display: "flex", alignItems: "center", justifyContent: "center", color: k.color }}>
                <k.icon size={19} />
              </div>
              <ArrowUpRight size={16} style={{ color: "var(--t-text-faint)" }} />
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: k.color, lineHeight: 1.1, marginTop: 14, textShadow: `0 0 18px ${k.color}55` }}>{k.value}</div>
            <div style={{ fontSize: 11.5, color: "var(--t-text-label)", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 3 }}>{k.label}</div>
            {k.sub && <div style={{ fontSize: 11.5, color: "var(--t-text-muted)", marginTop: 2 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Distribution panels */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginBottom: 18 }}>
        <DistPanel title="Cloudways servers" icon={Server} accent={NEON.cyan} rows={cwDist("server_label")} total={totals.cwApps} />
        <DistPanel title="Live target (WPE vs CW)" icon={Activity} accent={NEON.green} rows={cwDist("live_target")} total={totals.cwApps} />
        <DistPanel title="Hosting platforms" icon={Network} accent={NEON.indigo} rows={dist("hosting_platform")} total={totals.sites} />
        <DistPanel title="Cloudflare accounts" icon={Cloud} accent={NEON.violet} rows={dist("cloudflare")} total={totals.sites} />
      </div>

      {/* Top clients */}
      <div style={{ ...panel, overflow: "hidden" }}>
        <SectionHead title="Top clients by footprint" icon={Building2} accent={NEON.indigo} />
        <div style={{ padding: "6px 10px 12px" }}>
          {topClients.map((c) => (
            <div key={c.id} onClick={() => navigate(`/client-database/clients/${c.id}`)} className="cdb-row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, cursor: "pointer" }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: `linear-gradient(135deg, ${NEON.indigo}, ${NEON.violet})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#fff", fontSize: 14 }}>{c.name?.[0]?.toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: "var(--t-text-primary)", fontSize: 14 }}>{c.name}</div>
                <div style={{ ...mono, fontSize: 11, color: "var(--t-text-faint)" }}>{c.client_id}</div>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--t-text-muted)" }}>
                <span><b style={{ color: "#5b8cff" }}>{c.websites_count}</b> sites</span>
                <span><b style={{ color: NEON.cyan }}>{c.cloudflare_count}</b> CF</span>
                <span><b style={{ color: NEON.violet }}>{c.rules_count}</b> rules</span>
              </div>
              <ChevronLeft size={16} style={{ transform: "rotate(180deg)", color: "var(--t-text-faint)" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DistPanel({ title, icon: Icon, accent, rows, total }) {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <div style={{ ...panel, overflow: "hidden" }}>
      <SectionHead title={title} icon={Icon} accent={accent} />
      <div style={{ padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.length === 0 && <div style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>No data.</div>}
        {rows.map(([label, n]) => (
          <div key={label}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 4 }}>
              <span style={{ color: label === "—" ? "var(--t-text-faint)" : "var(--t-text-body)" }}>{label === "—" ? "Unset" : label}</span>
              <span style={{ color: "var(--t-text-muted)", ...mono }}>{n}{total ? ` · ${Math.round((n / total) * 100)}%` : ""}</span>
            </div>
            <div style={{ height: 6, borderRadius: 6, background: "var(--t-input)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(n / max) * 100}%`, borderRadius: 6, background: `linear-gradient(90deg, ${accent}, ${accent}aa)`, boxShadow: `0 0 10px ${accent}66` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionHead({ title, icon: Icon, accent, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 16px", borderBottom: "1px solid var(--t-border-soft)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, fontWeight: 700, fontSize: 14, color: "var(--t-text-primary)" }}>
        <Icon size={16} color={accent} /> {title}
      </div>
      {right}
    </div>
  );
}

/* ── Search results ───────────────────────────────────────── */
function SearchResults({ results, searching, query, navigate }) {
  const { sites, rules } = results;
  return (
    <div>
      <div style={{ fontSize: 12.5, color: "var(--t-text-label)", marginBottom: 12 }}>
        {searching ? "Searching…" : <>{sites.length} site{sites.length !== 1 ? "s" : ""} · {rules.length} rule{rules.length !== 1 ? "s" : ""}{query ? <> for <b style={{ color: "var(--t-text-body)" }}>"{query}"</b></> : ""}</>}
      </div>
      {sites.length > 0 && (
        <div style={{ ...panel, marginBottom: 16, overflow: "hidden" }}>
          <SectionHead title={`Websites (${sites.length})`} icon={Globe} accent={NEON.cyan} />
          <div className="cdb-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Domain</th><th style={thStyle}>Client</th><th style={thStyle}>Cloudflare</th><th style={thStyle}>DNS</th><th style={thStyle}>Hosting</th><th style={thStyle}>IP</th><th style={thStyle}>Status</th></tr></thead>
            <tbody>{sites.map((s) => (
              <tr key={s.id} className="cdb-row" onClick={() => navigate(`/client-database/clients/${s.client_pk}`)} style={{ cursor: "pointer" }}>
                <td style={{ ...tdStyle, fontWeight: 600, color: "var(--t-text-primary)" }}>{s.domain}</td>
                <td style={tdStyle}>{s.client_name}</td>
                <td style={tdStyle}><CloudflareBadge value={s.cloudflare} /></td>
                <td style={tdStyle}>{s.dns_control || "—"}</td>
                <td style={tdStyle}>{s.hosting_platform || "—"}</td>
                <td style={{ ...tdStyle, ...mono, color: "var(--t-text-muted)" }}>{s.ip_address || "—"}</td>
                <td style={tdStyle}><StatusPill value={s.status} /></td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}
      {rules.length > 0 && (
        <div style={{ ...panel, overflow: "hidden" }}>
          <SectionHead title={`Rules (${rules.length})`} icon={Shield} accent={NEON.violet} />
          <div className="cdb-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Domain</th><th style={thStyle}>Type</th><th style={thStyle}>Description</th><th style={thStyle}>Client</th></tr></thead>
            <tbody>{rules.map((r) => (
              <tr key={r.id} className="cdb-row" onClick={() => navigate(`/client-database/clients/${r.client_pk}`)} style={{ cursor: "pointer" }}>
                <td style={{ ...tdStyle, fontWeight: 600, color: "var(--t-text-primary)" }}>{r.domain}</td>
                <td style={tdStyle}><RuleTypePill value={r.rule_type} /></td>
                <td style={{ ...tdStyle, maxWidth: 480 }}>{r.description}</td>
                <td style={tdStyle}>{r.client_name}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}
      {!searching && sites.length === 0 && rules.length === 0 && (
        <EmptyState icon={Search} text={query ? `No matches for "${query}".` : "Start typing to search the registry."} />
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div style={{ ...panel, padding: 48, textAlign: "center", color: "var(--t-text-muted)" }}>
      <Icon size={30} style={{ color: "var(--t-text-faint)", marginBottom: 10 }} />
      <div style={{ fontSize: 13.5 }}>{text}</div>
    </div>
  );
}
function Spinner() {
  return <div style={{ padding: 50, textAlign: "center" }}><div style={{ display: "inline-block", width: 28, height: 28, border: `3px solid ${NEON.indigo}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /><style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style></div>;
}

/* ── Clients tab ──────────────────────────────────────────── */
function ClientsTab({ clients, reload, navigate }) {
  const [form, setForm] = useState(null);
  const sel = useRowSelection();
  const ids = useMemo(() => clients.map((c) => c.id), [clients]);
  const save = async (data, id) => { if (id) await cdbUpdateClient(id, data); else await cdbCreateClient(data); setForm(null); reload(); };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontSize: 12.5, color: "var(--t-text-label)" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer" }}><SelectAllBox ids={ids} sel={sel} /> {clients.length} clients</label>
          <SelectionInfo sel={sel} />
        </div>
        <div style={{ display: "inline-flex", gap: 8 }}>
          <ExportButton type="clients" ids={sel.ids()} />
          <button style={btnPrimary} onClick={() => setForm({})}><Plus size={15} /> Add Client</button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
        {clients.map((c) => (
          <div key={c.id} className="cdb-card" style={{ ...panel, padding: 18, cursor: "pointer", ...(sel.has(c.id) ? { borderColor: NEON.indigo, boxShadow: `0 0 0 1px ${NEON.indigo}, var(--t-shadow-panel)` } : {}) }} onClick={() => navigate(`/client-database/clients/${c.id}`)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0, background: `linear-gradient(135deg, ${NEON.indigo}, ${NEON.violet})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#fff", fontSize: 18, boxShadow: "0 0 16px rgba(79,111,255,0.4)" }}>{c.name?.[0]?.toUpperCase() || "?"}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--t-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                  <div style={{ ...mono, fontSize: 11, color: "var(--t-text-faint)", marginTop: 1 }}>{c.client_id}</div>
                </div>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }} onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" style={CB_STYLE} checked={sel.has(c.id)} onChange={() => sel.toggleOne(c.id)} title="Select for export" />
                <button onClick={(e) => { e.stopPropagation(); setForm(c); }} style={iconBtn}><Edit2 size={14} /></button>
              </div>
            </div>
            {(c.primary_contact || c.account_manager) && (
              <div style={{ fontSize: 12, color: "var(--t-text-muted)", marginTop: 12, display: "flex", flexDirection: "column", gap: 2 }}>
                {c.primary_contact && <span>Contact: {c.primary_contact}</span>}
                {c.account_manager && <span>AM: {c.account_manager}</span>}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <MiniTile label="Sites" value={c.websites_count} color="#5b8cff" />
              <MiniTile label="Cloudflare" value={c.cloudflare_count} color={NEON.cyan} />
              <MiniTile label="Rules" value={c.rules_count} color={NEON.violet} />
            </div>
          </div>
        ))}
      </div>
      {form && <ClientForm initial={form} onClose={() => setForm(null)} onSave={save} />}
    </div>
  );
}
function MiniTile({ label, value, color }) {
  return (
    <div style={{ flex: 1, padding: "10px 6px", textAlign: "center", borderRadius: 11, background: "var(--t-input)", border: `1px solid ${color}33` }}>
      <div style={{ fontSize: 19, fontWeight: 800, color, textShadow: `0 0 12px ${color}55` }}>{value}</div>
      <div style={{ fontSize: 9.5, color: "var(--t-text-label)", textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 2 }}>{label}</div>
    </div>
  );
}

function ClientForm({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    client_id: initial.client_id || "", name: initial.name || "", primary_contact: initial.primary_contact || "",
    email: initial.email || "", phone: initial.phone || "", account_manager: initial.account_manager || "",
  });
  const [err, setErr] = useState(""); const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => { setErr(""); setSaving(true); try { await onSave(f, initial.id); } catch (e) { setErr(e?.response?.data?.error || "Save failed"); setSaving(false); } };
  return (
    <Modal title={initial.id ? `Edit ${initial.name}` : "Add Client"} subtitle={initial.id ? initial.client_id : "New client record"} onClose={onClose}>
      <FormError msg={err} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Client ID" required><Input value={f.client_id} onChange={set("client_id")} placeholder="C-014" style={mono} /></Field>
        <Field label="Name" required><Input value={f.name} onChange={set("name")} /></Field>
        <Field label="Primary Contact"><Input value={f.primary_contact} onChange={set("primary_contact")} /></Field>
        <Field label="Account Manager"><Input value={f.account_manager} onChange={set("account_manager")} /></Field>
        <Field label="Email"><Input value={f.email} onChange={set("email")} /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={set("phone")} /></Field>
      </div>
      <ModalActions onClose={onClose} onSubmit={submit} saving={saving} />
    </Modal>
  );
}
const ModalActions = ({ onClose, onSubmit, saving }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 6 }}>
    <button style={btnGhost} onClick={onClose}>Cancel</button>
    <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, pointerEvents: saving ? "none" : "auto" }} onClick={onSubmit}>{saving ? "Saving…" : "Save"}</button>
  </div>
);

/* ── Websites tab ─────────────────────────────────────────── */
function WebsitesTab({ clients, enums, navigate }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ client_id: "", cloudflare: "", hosting_platform: "", dns_control: "", status: "", q: "" });
  const [sort, setSort] = useState({ col: "domain", dir: "asc" });
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const sel = useRowSelection();
  const visibleIds = useMemo(() => rows.map((w) => w.id), [rows]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { sort: sort.col, dir: sort.dir };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    cdbGetWebsites(params).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters, sort]);
  useEffect(() => { const t = setTimeout(load, 180); return () => clearTimeout(t); }, [load]);

  const toggleSort = (col) => setSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));
  const arrow = (col) => sort.col === col ? <span style={{ color: NEON.cyan }}>{sort.dir === "asc" ? " ↑" : " ↓"}</span> : "";
  const save = async (data, id) => { if (id) await cdbUpdateWebsite(id, data); else await cdbCreateWebsite(data); setForm(null); load(); };
  const remove = async (w) => { if (window.confirm(`Delete ${w.domain} and its rules?`)) { await cdbDeleteWebsite(w.id); load(); } };
  const clearable = Object.values(filters).some(Boolean);

  if (!enums) return <Spinner />;
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <Filter size={15} style={{ color: "var(--t-text-muted)" }} />
        <FilterSelect value={filters.client_id} onChange={(v) => setFilters({ ...filters, client_id: v })} placeholder="All clients" options={clients.map((c) => [String(c.id), c.name])} />
        <FilterSelect value={filters.cloudflare} onChange={(v) => setFilters({ ...filters, cloudflare: v })} placeholder="Any Cloudflare" options={enums.cloudflare.map((x) => [x, x])} />
        <FilterSelect value={filters.dns_control} onChange={(v) => setFilters({ ...filters, dns_control: v })} placeholder="Any DNS" options={enums.dns_control.map((x) => [x, x])} />
        <FilterSelect value={filters.hosting_platform} onChange={(v) => setFilters({ ...filters, hosting_platform: v })} placeholder="Any hosting" options={enums.hosting_platform.map((x) => [x, x])} />
        <FilterSelect value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} placeholder="Any status" options={enums.status.map((x) => [x, x])} />
        <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Filter domain/notes…" style={{ width: "auto", flex: "1 1 160px", minWidth: 140, padding: "7px 11px" }} />
        {clearable && <button style={btnGhost} onClick={() => setFilters({ client_id: "", cloudflare: "", hosting_platform: "", dns_control: "", status: "", q: "" })}>Clear</button>}
        <div style={{ flex: 1 }} />
        <ExportButton type="websites" ids={sel.ids()} />
        <button style={btnPrimary} onClick={() => setForm({})}><Plus size={15} /> Add Website</button>
      </div>
      <div style={{ ...panel, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid var(--t-border-soft)", fontSize: 12, color: "var(--t-text-label)" }}>
          <span>{rows.length} website{rows.length !== 1 ? "s" : ""}</span>
          <SelectionInfo sel={sel} />
        </div>
        <div className="cdb-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={{ ...thStyle, width: 34, textAlign: "center" }}><SelectAllBox ids={visibleIds} sel={sel} /></th>
            <th style={thSortable} onClick={() => toggleSort("domain")}>Domain{arrow("domain")}</th>
            <th style={thSortable} onClick={() => toggleSort("client")}>Client{arrow("client")}</th>
            <th style={thSortable} onClick={() => toggleSort("cloudflare")}>Cloudflare{arrow("cloudflare")}</th>
            <th style={thSortable} onClick={() => toggleSort("dns_control")}>DNS{arrow("dns_control")}</th>
            <th style={thSortable} onClick={() => toggleSort("hosting_platform")}>Hosting{arrow("hosting_platform")}</th>
            <th style={thStyle}>IP</th>
            <th style={thStyle}>SSL</th>
            <th style={thSortable} onClick={() => toggleSort("status")}>Status{arrow("status")}</th>
            <th style={thStyle}>Rules</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
          </tr></thead>
          <tbody>{rows.map((w) => (
            <WebsiteRow key={w.id} w={w} sel={sel} navigate={navigate} onEdit={() => setForm(w)} onDelete={() => remove(w)} onAddRule={() => setForm(null)} />
          ))}</tbody>
        </table></div>
        {!loading && rows.length === 0 && <div style={{ padding: 32, textAlign: "center", color: "var(--t-text-muted)", fontSize: 13 }}>No websites match these filters.</div>}
      </div>
      {form && <WebsiteForm initial={form} clients={clients} enums={enums} onClose={() => setForm(null)} onSave={save} />}
    </div>
  );
}
// Expandable website row: click to reveal full detail + its rules inline.
function WebsiteRow({ w, sel, navigate, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    const next = !open; setOpen(next);
    if (next && !detail) {
      setLoading(true);
      try { const r = await cdbGetWebsite(w.id); setDetail(r.data); } catch {} finally { setLoading(false); }
    }
  };
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  const rules = detail?.rules || [];
  const bb = open ? "none" : tdStyle.borderBottom;
  return (
    <>
      <tr className="cdb-row" onClick={toggle} style={{ cursor: "pointer", background: open ? "rgba(79,111,255,0.07)" : sel?.has(w.id) ? "rgba(79,111,255,0.10)" : undefined }}>
        <td style={{ ...tdStyle, textAlign: "center", borderBottom: bb }} onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" style={CB_STYLE} checked={sel?.has(w.id) || false} onChange={() => sel?.toggleOne(w.id)} />
        </td>
        <td style={{ ...tdStyle, fontWeight: 600, color: "var(--t-text-primary)", borderBottom: open ? "none" : tdStyle.borderBottom }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
            <ChevronDown size={14} style={{ color: NEON.cyan, transform: open ? "none" : "rotate(-90deg)", transition: "transform .15s", flexShrink: 0 }} />
            <a href={`https://${w.domain}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "inherit", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}>{w.domain} <ExternalLink size={11} style={{ color: "var(--t-text-faint)" }} /></a>
          </span>
        </td>
        <td style={{ ...tdStyle, borderBottom: open ? "none" : tdStyle.borderBottom }}><span style={{ cursor: "pointer", color: NEON.indigo }} onClick={stop(() => navigate(`/client-database/clients/${w.client_id}`))}>{w.client_name}</span></td>
        <td style={{ ...tdStyle, borderBottom: open ? "none" : tdStyle.borderBottom }}><CloudflareBadge value={w.cloudflare} /></td>
        <td style={{ ...tdStyle, borderBottom: open ? "none" : tdStyle.borderBottom }}>{w.dns_control || "—"}</td>
        <td style={{ ...tdStyle, borderBottom: open ? "none" : tdStyle.borderBottom }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {w.hosting_platform || "—"}
            {w.cw_gps && <GpsBadge on compact />}
            {w.cw_manage_url && <CloudwaysLink url={w.cw_manage_url} small label="CW" />}
          </span>
        </td>
        <td style={{ ...tdStyle, ...mono, color: "var(--t-text-muted)", borderBottom: open ? "none" : tdStyle.borderBottom }}>{w.ip_address || "—"}</td>
        <td style={{ ...tdStyle, borderBottom: open ? "none" : tdStyle.borderBottom }}><SslPill value={w.ssl} /></td>
        <td style={{ ...tdStyle, borderBottom: open ? "none" : tdStyle.borderBottom }}><StatusPill value={w.status} /></td>
        <td style={{ ...tdStyle, ...mono, color: "var(--t-text-muted)", borderBottom: open ? "none" : tdStyle.borderBottom }}>{w.rules_count}</td>
        <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap", borderBottom: open ? "none" : tdStyle.borderBottom }}>
          <button style={{ ...iconBtn, marginRight: 5 }} onClick={stop(onEdit)}><Edit2 size={13} /></button>
          <button style={{ ...iconBtn, color: "var(--badge-red)", borderColor: "rgba(248,113,113,0.3)" }} onClick={stop(onDelete)}><Trash2 size={13} /></button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={11} style={{ padding: 0, background: "var(--t-surface-deep, var(--t-input))", borderBottom: "2px solid rgba(79,111,255,0.25)", boxShadow: "inset 0 0 30px rgba(79,111,255,0.06)" }}>
            <div className="cdb-rise" style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <div style={{ ...mono, fontSize: 12, color: "var(--t-text-faint)" }}>{w.site_id}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={btnPrimary} onClick={stop(onEdit)}><Edit2 size={14} /> Edit website</button>
                  <button style={btnDanger} onClick={stop(onDelete)}><Trash2 size={14} /> Delete</button>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
                <Info label="Cloudflare" value={w.cloudflare} />
                <Info label="DNS Control" value={w.dns_control} />
                <Info label="Hosting Platform" value={w.hosting_platform} />
                <Info label="Hosting Location" value={w.hosting_location} />
                <Info label="IP Address" value={w.ip_address} mono />
                <Info label="SSL" value={w.ssl} />
                <Info label="Status" value={w.status} />
              </div>
              {detail?.cloudways_apps?.length > 0 && <CloudwaysAppBlock apps={detail.cloudways_apps} />}
              {w.notes && (
                <div>
                  <div style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 6 }}><FileText size={12} /> Notes</div>
                  <div style={{ fontSize: 13, color: "var(--t-text-body)", background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10, padding: "10px 13px", lineHeight: 1.5 }}>{w.notes}</div>
                </div>
              )}
              <div>
                <div style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}><Shield size={12} /> Rules ({loading ? "…" : rules.length})</div>
                {loading && <div style={{ fontSize: 12.5, color: "var(--t-text-muted)" }}>Loading rules…</div>}
                {!loading && rules.length === 0 && <div style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>No rules on this site.</div>}
                {!loading && rules.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {rules.map((r) => (
                      <div key={r.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "var(--t-panel)", border: "1px solid var(--t-border-softer)", borderRadius: 10, padding: "9px 12px" }}>
                        <span style={{ ...mono, fontSize: 11, color: "var(--t-text-faint)", flexShrink: 0, paddingTop: 3 }}>{r.rule_id}</span>
                        <div style={{ flexShrink: 0 }}><RuleTypePill value={r.rule_type} /></div>
                        <div style={{ fontSize: 12.5, color: "var(--t-text-body)", lineHeight: 1.45 }}>{r.description}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function FilterSelect({ value, onChange, options, placeholder }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="cdb-input" style={{ ...inputStyle, width: "auto", padding: "7px 10px", fontSize: 12.5, appearance: "auto", maxWidth: 180 }}>
      <option value="">{placeholder}</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
function WebsiteForm({ initial, clients, enums, onClose, onSave }) {
  const [f, setF] = useState({
    site_id: initial.site_id || "", client_id: initial.client_id || "", domain: initial.domain || "",
    cloudflare: initial.cloudflare || "", dns_control: initial.dns_control || "", hosting_platform: initial.hosting_platform || "",
    hosting_location: initial.hosting_location || "", ip_address: initial.ip_address || "", ssl: initial.ssl || "", status: initial.status || "", notes: initial.notes || "",
  });
  const [err, setErr] = useState(""); const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => { setErr(""); setSaving(true); try { await onSave({ ...f, client_id: f.client_id ? Number(f.client_id) : null }, initial.id); } catch (e) { setErr(e?.response?.data?.error || "Save failed"); setSaving(false); } };
  return (
    <Modal title={initial.id ? `Edit ${initial.domain}` : "Add Website"} subtitle={initial.id ? initial.site_id : "New website record"} onClose={onClose} width={660}>
      <FormError msg={err} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Site ID" required><Input value={f.site_id} onChange={set("site_id")} placeholder="S-0249" style={mono} /></Field>
        <Field label="Client" required>
          <select value={f.client_id || ""} onChange={set("client_id")} className="cdb-input" style={{ ...inputStyle, appearance: "auto" }}>
            <option value="">— select client —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.client_id})</option>)}
          </select>
        </Field>
        <Field label="Domain" required span={2}><Input value={f.domain} onChange={set("domain")} placeholder="example.com" /></Field>
        <Field label="Cloudflare"><Select value={f.cloudflare} onChange={set("cloudflare")} options={enums.cloudflare} /></Field>
        <Field label="DNS Control"><Select value={f.dns_control} onChange={set("dns_control")} options={enums.dns_control} /></Field>
        <Field label="Hosting Platform"><Select value={f.hosting_platform} onChange={set("hosting_platform")} options={enums.hosting_platform} /></Field>
        <Field label="Hosting Location"><Input value={f.hosting_location} onChange={set("hosting_location")} placeholder="e.g. US-East" /></Field>
        <Field label="IP Address"><Input value={f.ip_address} onChange={set("ip_address")} placeholder="e.g. 174.138.87.156" style={mono} /></Field>
        <Field label="SSL"><Select value={f.ssl} onChange={set("ssl")} options={enums.ssl} /></Field>
        <Field label="Status"><Select value={f.status} onChange={set("status")} options={enums.status} /></Field>
      </div>
      <Field label="Notes"><Textarea value={f.notes} onChange={set("notes")} /></Field>
      <ModalActions onClose={onClose} onSubmit={submit} saving={saving} />
    </Modal>
  );
}

/* ── Cloudways tab ────────────────────────────────────────── */
function CloudwaysTab({ navigate }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ servers: [], live_targets: [] });
  const [websites, setWebsites] = useState([]);
  const [filters, setFilters] = useState({ server: "", live: "", gps: "", linked: "", q: "" });
  const [sort, setSort] = useState({ col: "server", dir: "asc" });
  const [loading, setLoading] = useState(true);
  const sel = useRowSelection();

  useEffect(() => { cdbCloudwaysMeta().then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { cdbGetWebsites({}).then((r) => setWebsites(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { sort: sort.col, dir: sort.dir };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    cdbGetCloudwaysApps(params).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters, sort]);
  useEffect(() => { const t = setTimeout(load, 180); return () => clearTimeout(t); }, [load]);

  const toggleSort = (col) => setSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));
  const arrow = (col) => sort.col === col ? <span style={{ color: NEON.cyan }}>{sort.dir === "asc" ? " ↑" : " ↓"}</span> : "";
  const clearable = Object.values(filters).some(Boolean);

  const setLink = async (app, websiteId) => {
    try { await cdbUpdateCloudwaysApp(app.id, { website_id: websiteId || null }); load(); } catch (e) { alert(e?.response?.data?.error || "Failed"); }
  };

  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const sortedSites = useMemo(() => [...websites].sort((a, b) => a.domain.localeCompare(b.domain)), [websites]);
  const linkedCount = rows.filter((r) => r.website_id).length;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <Filter size={15} style={{ color: "var(--t-text-muted)" }} />
        <FilterSelect value={filters.server} onChange={(v) => setFilters({ ...filters, server: v })} placeholder="Any server"
          options={meta.servers.map((s) => [s.server_label, `${s.server_label} (${s.apps_count})`])} />
        <FilterSelect value={filters.live} onChange={(v) => setFilters({ ...filters, live: v })} placeholder="Any live target"
          options={meta.live_targets.map((x) => [x, x === "WPE" ? "WP Engine" : x])} />
        <FilterSelect value={filters.gps} onChange={(v) => setFilters({ ...filters, gps: v })} placeholder="GPS: any"
          options={[["true", "Uses GPS"], ["false", "No GPS"]]} />
        <FilterSelect value={filters.linked} onChange={(v) => setFilters({ ...filters, linked: v })} placeholder="Any link state"
          options={[["true", "Linked to a site"], ["false", "Unlinked"]]} />
        <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Filter app/domain/server/IP…" style={{ width: "auto", flex: "1 1 180px", minWidth: 150, padding: "7px 11px" }} />
        {clearable && <button style={btnGhost} onClick={() => setFilters({ server: "", live: "", gps: "", linked: "", q: "" })}>Clear</button>}
        <div style={{ flex: 1 }} />
        <ExportButton type="cloudways" ids={sel.ids()} />
      </div>

      <div style={{ ...panel, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid var(--t-border-soft)", fontSize: 12, color: "var(--t-text-label)" }}>
          <span>{rows.length} Cloudways app{rows.length !== 1 ? "s" : ""} · {linkedCount} linked · {rows.length - linkedCount} unlinked</span>
          <SelectionInfo sel={sel} />
        </div>
        <div className="cdb-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={{ ...thStyle, width: 34, textAlign: "center" }}><SelectAllBox ids={visibleIds} sel={sel} /></th>
            <th style={thSortable} onClick={() => toggleSort("domain")}>Application{arrow("domain")}</th>
            <th style={thSortable} onClick={() => toggleSort("app_id")}>App ID{arrow("app_id")}</th>
            <th style={thSortable} onClick={() => toggleSort("server")}>Server{arrow("server")}</th>
            <th style={thStyle}>IP</th>
            <th style={thSortable} onClick={() => toggleSort("gps")}>GPS{arrow("gps")}</th>
            <th style={thSortable} onClick={() => toggleSort("live")}>Live target{arrow("live")}</th>
            <th style={thStyle}>Manage</th>
            <th style={thStyle}>Link to site</th>
          </tr></thead>
          <tbody>{rows.map((a) => (
            <tr key={a.id} className="cdb-row" style={{ background: sel.has(a.id) ? "rgba(79,111,255,0.10)" : undefined }}>
              <td style={{ ...tdStyle, textAlign: "center" }}>
                <input type="checkbox" style={CB_STYLE} checked={sel.has(a.id)} onChange={() => sel.toggleOne(a.id)} />
              </td>
              <td style={{ ...tdStyle, fontWeight: 600, color: "var(--t-text-primary)" }}>
                {a.domain ? (
                  <a href={`https://${a.domain}`} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    {a.domain} <ExternalLink size={11} style={{ color: "var(--t-text-faint)" }} />
                  </a>
                ) : (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                    <span style={{ color: "var(--t-text-muted)" }}>{a.app_label || a.app_fqdn || "—"}</span>
                    <Pill text="unlinked" color={NEON.amber} bg="rgba(251,191,36,0.13)" />
                  </span>
                )}
              </td>
              <td style={{ ...tdStyle, ...mono, fontSize: 12, color: "var(--t-text-muted)" }}>{a.app_id}</td>
              <td style={tdStyle}>{a.server_label || "—"}</td>
              <td style={{ ...tdStyle, ...mono, color: "var(--t-text-muted)" }}>{a.server_ip || "—"}</td>
              <td style={tdStyle}><GpsBadge on={a.gps_access} /></td>
              <td style={tdStyle}><LiveTargetPill value={a.live_target} /></td>
              <td style={tdStyle}><CloudwaysLink url={a.manage_url} label="Cloudways" /></td>
              <td style={{ ...tdStyle, minWidth: 170 }}>
                {a.website_id ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: NEON.green }}><Link2 size={12} /> linked</span>
                    <button title="Unlink" style={{ ...iconBtn, width: 26, height: 26 }} onClick={() => setLink(a, null)}><Unlink size={12} /></button>
                  </span>
                ) : (
                  <select value="" onChange={(e) => e.target.value && setLink(a, Number(e.target.value))} className="cdb-input"
                    style={{ ...inputStyle, width: "auto", maxWidth: 170, padding: "5px 8px", fontSize: 12, appearance: "auto" }}>
                    <option value="">Link to site…</option>
                    {sortedSites.map((w) => <option key={w.id} value={w.id}>{w.domain}</option>)}
                  </select>
                )}
              </td>
            </tr>
          ))}</tbody>
        </table></div>
        {loading && <Spinner />}
        {!loading && rows.length === 0 && <div style={{ padding: 32, textAlign: "center", color: "var(--t-text-muted)", fontSize: 13 }}>No Cloudways apps match these filters.</div>}
      </div>
    </div>
  );
}

/* ── Rules tab ────────────────────────────────────────────── */
function RulesTab({ clients, enums }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ client_id: "", domain: "", rule_type: "", q: "" });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [websites, setWebsites] = useState([]);
  const sel = useRowSelection();
  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    cdbGetRules(params).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const openForm = async (rule) => { if (!websites.length) { try { const r = await cdbGetWebsites({}); setWebsites(r.data); } catch {} } setForm(rule || {}); };
  const save = async (data, id) => { if (id) await cdbUpdateRule(id, data); else await cdbCreateRule(data); setForm(null); load(); };
  const remove = async (r) => { if (window.confirm(`Delete rule ${r.rule_id}?`)) { await cdbDeleteRule(r.id); load(); } };
  const clearable = Object.values(filters).some(Boolean);

  if (!enums) return <Spinner />;
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <Filter size={15} style={{ color: "var(--t-text-muted)" }} />
        <FilterSelect value={filters.client_id} onChange={(v) => setFilters({ ...filters, client_id: v })} placeholder="All clients" options={clients.map((c) => [String(c.id), c.name])} />
        <FilterSelect value={filters.rule_type} onChange={(v) => setFilters({ ...filters, rule_type: v })} placeholder="Any type" options={enums.rule_type.map((x) => [x, x])} />
        <Input value={filters.domain} onChange={(e) => setFilters({ ...filters, domain: e.target.value })} placeholder="Filter by domain…" style={{ width: "auto", flex: "0 1 180px", minWidth: 140, padding: "7px 11px" }} />
        <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search descriptions…" style={{ width: "auto", flex: "1 1 180px", minWidth: 160, padding: "7px 11px" }} />
        {clearable && <button style={btnGhost} onClick={() => setFilters({ client_id: "", domain: "", rule_type: "", q: "" })}>Clear</button>}
        <div style={{ flex: 1 }} />
        <ExportButton type="rules" ids={sel.ids()} />
        <button style={btnPrimary} onClick={() => openForm(null)}><Plus size={15} /> Add Rule</button>
      </div>
      <div style={{ ...panel, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid var(--t-border-soft)", fontSize: 12, color: "var(--t-text-label)" }}>
          <span>{rows.length} rule{rows.length !== 1 ? "s" : ""}</span>
          <SelectionInfo sel={sel} />
        </div>
        <div className="cdb-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={{ ...thStyle, width: 34, textAlign: "center" }}><SelectAllBox ids={visibleIds} sel={sel} /></th><th style={thStyle}>Rule ID</th><th style={thStyle}>Domain</th><th style={thStyle}>Type</th><th style={thStyle}>Description</th><th style={thStyle}>Client</th><th style={{ ...thStyle, textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="cdb-row" style={{ background: sel.has(r.id) ? "rgba(79,111,255,0.10)" : undefined }}>
              <td style={{ ...tdStyle, textAlign: "center" }}><input type="checkbox" style={CB_STYLE} checked={sel.has(r.id)} onChange={() => sel.toggleOne(r.id)} /></td>
              <td style={{ ...tdStyle, ...mono, fontSize: 12, color: "var(--t-text-muted)" }}>{r.rule_id}</td>
              <td style={{ ...tdStyle, fontWeight: 600, color: "var(--t-text-primary)" }}>{r.domain}</td>
              <td style={tdStyle}><RuleTypePill value={r.rule_type} /></td>
              <td style={{ ...tdStyle, maxWidth: 520 }}>{r.description}</td>
              <td style={tdStyle}>{r.client_name}</td>
              <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                <button style={{ ...iconBtn, marginRight: 5 }} onClick={() => openForm(r)}><Edit2 size={13} /></button>
                <button style={{ ...iconBtn, color: "var(--badge-red)", borderColor: "rgba(248,113,113,0.3)" }} onClick={() => remove(r)}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
        {!loading && rows.length === 0 && <div style={{ padding: 32, textAlign: "center", color: "var(--t-text-muted)", fontSize: 13 }}>No rules match these filters.</div>}
      </div>
      {form && <RuleForm initial={form} websites={websites} enums={enums} onClose={() => setForm(null)} onSave={save} />}
    </div>
  );
}
function RuleForm({ initial, websites, enums, onClose, onSave }) {
  const [f, setF] = useState({ rule_id: initial.rule_id || "", website_id: initial.website_id || "", rule_type: initial.rule_type || "", description: initial.description || "" });
  const [err, setErr] = useState(""); const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => { setErr(""); setSaving(true); try { await onSave({ ...f, website_id: f.website_id ? Number(f.website_id) : null }, initial.id); } catch (e) { setErr(e?.response?.data?.error || "Save failed"); setSaving(false); } };
  const sorted = useMemo(() => [...websites].sort((a, b) => a.domain.localeCompare(b.domain)), [websites]);
  return (
    <Modal title={initial.id ? `Edit ${initial.rule_id}` : "Add Rule"} subtitle={initial.id ? initial.domain : "New rule record"} onClose={onClose}>
      <FormError msg={err} />
      <Field label="Rule ID" required><Input value={f.rule_id} onChange={set("rule_id")} placeholder="R-0586" style={mono} /></Field>
      <Field label="Website (domain)" required>
        <select value={f.website_id || ""} onChange={set("website_id")} className="cdb-input" style={{ ...inputStyle, appearance: "auto" }}>
          <option value="">— select website —</option>
          {sorted.map((w) => <option key={w.id} value={w.id}>{w.domain}{w.client_name ? ` — ${w.client_name}` : ""}</option>)}
        </select>
      </Field>
      <Field label="Rule Type"><Select value={f.rule_type} onChange={set("rule_type")} options={enums.rule_type} /></Field>
      <Field label="Description"><Textarea value={f.description} onChange={set("description")} style={{ minHeight: 96 }} /></Field>
      <ModalActions onClose={onClose} onSubmit={submit} saving={saving} />
    </Modal>
  );
}

/* ── Client detail — the workbook "Lookup" card ───────────── */
function ClientDetail({ clientPk, enums, clients, onBack, reloadClients }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [editClient, setEditClient] = useState(false);
  const [wForm, setWForm] = useState(null);
  const [rForm, setRForm] = useState(null);

  const load = useCallback(() => { cdbGetClient(clientPk).then((r) => setData(r.data)).catch(() => setErr("Client not found")); }, [clientPk]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div style={{ padding: 28, width: "100%", maxWidth: 1840, margin: "0 auto", boxSizing: "border-box" }}><button style={btnGhost} onClick={onBack}><ChevronLeft size={15} /> Back</button><EmptyState icon={Building2} text={err} /></div>;
  if (!data) return <Spinner />;
  const { client, stats, websites, rules } = data;

  const saveClient = async (d, id) => { await cdbUpdateClient(id, d); setEditClient(false); load(); reloadClients(); };
  const delClient = async () => {
    const msg = stats.websites_count > 0
      ? `Delete ${client.name}? This also deletes ${stats.websites_count} website(s) and ${stats.rules_count} rule(s). This cannot be undone.`
      : `Delete ${client.name}?`;
    if (!window.confirm(msg)) return;
    try { await cdbDeleteClient(client.id, stats.websites_count > 0); reloadClients(); onBack(); }
    catch (e) { alert(e?.response?.data?.error || "Delete failed"); }
  };
  const saveWebsite = async (d, id) => { if (id) await cdbUpdateWebsite(id, d); else await cdbCreateWebsite(d); setWForm(null); load(); reloadClients(); };
  const saveRule = async (d, id) => { if (id) await cdbUpdateRule(id, d); else await cdbCreateRule(d); setRForm(null); load(); reloadClients(); };
  const delWebsite = async (w) => { if (window.confirm(`Delete ${w.domain} and its rules?`)) { await cdbDeleteWebsite(w.id); load(); reloadClients(); } };
  const delRule = async (r) => { if (window.confirm(`Delete rule ${r.rule_id}?`)) { await cdbDeleteRule(r.id); load(); reloadClients(); } };

  return (
    <div style={{ padding: isMobile ? 14 : 28, width: "100%", maxWidth: 1840, margin: "0 auto", boxSizing: "border-box" }}>
      <ScopedStyles />
      <button style={{ ...btnGhost, marginBottom: 16 }} onClick={onBack}><ChevronLeft size={15} /> All clients</button>

      {/* Profile / lookup card */}
      <div style={{ ...glassPanel, padding: isMobile ? 18 : 24, marginBottom: 18, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.5, pointerEvents: "none", backgroundImage: "radial-gradient(circle at 92% -30%, rgba(0,212,255,0.13), transparent 40%)" }} />
        <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", gap: 15, alignItems: "center" }}>
            <div style={{ width: 54, height: 54, borderRadius: 14, background: `linear-gradient(135deg, ${NEON.indigo}, ${NEON.violet})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 24, color: "#fff", boxShadow: "0 0 22px rgba(79,111,255,0.5)" }}>{client.name?.[0]?.toUpperCase() || "?"}</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, color: "var(--t-text-primary)" }}>{client.name}</h1>
              <div style={{ ...mono, fontSize: 12, color: "var(--t-text-faint)", marginTop: 2 }}>{client.client_id}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 9 }}>
            <button style={btnGhost} onClick={() => setEditClient(true)}><Edit2 size={14} /> Edit</button>
            <button style={btnDanger} onClick={delClient}><Trash2 size={14} /> Delete</button>
          </div>
        </div>
        <div style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginTop: 20 }}>
          <Info label="Primary Contact" value={client.primary_contact} />
          <Info label="Email" value={client.email} link={client.email ? `mailto:${client.email}` : null} />
          <Info label="Phone" value={client.phone} />
          <Info label="Account Manager" value={client.account_manager} />
        </div>
        <div style={{ position: "relative", display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
          <BigTile icon={Globe} label="Websites" value={stats.websites_count} color="#5b8cff" />
          <BigTile icon={Cloud} label="On Cloudflare" value={stats.cloudflare_count} color={NEON.cyan} />
          <BigTile icon={ShieldCheck} label="Security Rules" value={stats.rules_count} color={NEON.violet} />
        </div>
      </div>

      {/* Websites */}
      <div style={{ ...panel, overflow: "hidden", marginBottom: 18 }}>
        <SectionHead title={`Websites (${websites.length})`} icon={Globe} accent={NEON.cyan} right={<button style={btnGhost} onClick={() => setWForm({ client_id: client.id })}><Plus size={14} /> Add Website</button>} />
        <div className="cdb-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={thStyle}>Domain</th><th style={thStyle}>Cloudflare</th><th style={thStyle}>DNS</th><th style={thStyle}>Hosting</th><th style={thStyle}>Cloudways</th><th style={thStyle}>Location</th><th style={thStyle}>IP</th><th style={thStyle}>SSL</th><th style={thStyle}>Status</th><th style={thStyle}>Rules</th><th style={{ ...thStyle, textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>{websites.map((w) => (
            <tr key={w.id} className="cdb-row">
              <td style={{ ...tdStyle, fontWeight: 600, color: "var(--t-text-primary)" }}><a href={`https://${w.domain}`} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}>{w.domain} <ExternalLink size={11} style={{ color: "var(--t-text-faint)" }} /></a></td>
              <td style={tdStyle}><CloudflareBadge value={w.cloudflare} /></td>
              <td style={tdStyle}>{w.dns_control || "—"}</td>
              <td style={tdStyle}>{w.hosting_platform || "—"}</td>
              <td style={tdStyle}>
                {w.cw_manage_url ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {w.cw_gps && <GpsBadge on compact />}
                    <CloudwaysLink url={w.cw_manage_url} small label="Cloudways" />
                  </span>
                ) : <span style={{ color: "var(--t-text-faint)" }}>—</span>}
              </td>
              <td style={tdStyle}>{w.hosting_location || "—"}</td>
              <td style={{ ...tdStyle, ...mono, color: "var(--t-text-muted)" }}>{w.ip_address || "—"}</td>
              <td style={tdStyle}><SslPill value={w.ssl} /></td>
              <td style={tdStyle}><StatusPill value={w.status} /></td>
              <td style={{ ...tdStyle, ...mono, color: "var(--t-text-muted)" }}>{w.rules_count}</td>
              <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                <button style={{ ...iconBtn, marginRight: 5 }} onClick={() => setWForm(w)}><Edit2 size={13} /></button>
                <button style={{ ...iconBtn, color: "var(--badge-red)", borderColor: "rgba(248,113,113,0.3)" }} onClick={() => delWebsite(w)}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
        {websites.length === 0 && <div style={{ padding: 26, textAlign: "center", color: "var(--t-text-muted)", fontSize: 13 }}>No websites yet.</div>}
      </div>

      {/* Rules */}
      <div style={{ ...panel, overflow: "hidden", marginBottom: 18 }}>
        <SectionHead title={`Rules (${rules.length})`} icon={Shield} accent={NEON.violet} right={<button style={btnGhost} onClick={() => setRForm({})}><Plus size={14} /> Add Rule</button>} />
        <div className="cdb-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={thStyle}>Rule ID</th><th style={thStyle}>Domain</th><th style={thStyle}>Type</th><th style={thStyle}>Description</th><th style={{ ...thStyle, textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>{rules.map((r) => (
            <tr key={r.id} className="cdb-row">
              <td style={{ ...tdStyle, ...mono, fontSize: 12, color: "var(--t-text-muted)" }}>{r.rule_id}</td>
              <td style={{ ...tdStyle, fontWeight: 600, color: "var(--t-text-primary)" }}>{r.domain}</td>
              <td style={tdStyle}><RuleTypePill value={r.rule_type} /></td>
              <td style={{ ...tdStyle, maxWidth: 560 }}>{r.description}</td>
              <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                <button style={{ ...iconBtn, marginRight: 5 }} onClick={() => setRForm(r)}><Edit2 size={13} /></button>
                <button style={{ ...iconBtn, color: "var(--badge-red)", borderColor: "rgba(248,113,113,0.3)" }} onClick={() => delRule(r)}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
        {rules.length === 0 && <div style={{ padding: 26, textAlign: "center", color: "var(--t-text-muted)", fontSize: 13 }}>No rules yet.</div>}
      </div>

      {editClient && <ClientForm initial={client} onClose={() => setEditClient(false)} onSave={saveClient} />}
      {wForm && enums && <WebsiteForm initial={wForm} clients={clients} enums={enums} onClose={() => setWForm(null)} onSave={saveWebsite} />}
      {rForm && enums && <RuleForm initial={rForm} websites={websites} enums={enums} onClose={() => setRForm(null)} onSave={saveRule} />}
    </div>
  );
}
function Info({ label, value, link, mono: isMono }) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      {link && value
        ? <a href={link} style={{ fontSize: 14, color: NEON.indigo, textDecoration: "none" }}>{value}</a>
        : <div style={{ fontSize: 14, ...(isMono && value ? mono : {}), color: value ? "var(--t-text-primary)" : "var(--t-text-faint)" }}>{value || "—"}</div>}
    </div>
  );
}
function BigTile({ icon: Icon, label, value, color }) {
  return (
    <div style={{ flex: 1, minWidth: 150, padding: 18, borderRadius: 14, background: "var(--t-input)", border: `1px solid ${color}40`, display: "flex", alignItems: "center", gap: 15, boxShadow: `inset 0 0 26px ${color}12` }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}1f`, display: "flex", alignItems: "center", justifyContent: "center", color }}><Icon size={22} /></div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1, textShadow: `0 0 18px ${color}55` }}>{value}</div>
        <div style={{ fontSize: 11, color: "var(--t-text-label)", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 5 }}>{label}</div>
      </div>
    </div>
  );
}
